<template>
  <ScrollableContainer class="ufo-sepa--cop-det cursor-settings">
    <div class="ufo-sepa--cop-list">
      <div
        v-for="(title, key) in cursorAlternatives"
        :key="key"
        :class="['cursor-preset-preview', { selected: cursorType === key }]"
        @click="changeCursorPreset(key)"
      >
        <div class="cursor-preview">
          <div
            v-for="state in ['preview_1', 'preview_2']"
            :key="state"
            class="cursor-preview-item"
          >
            <div class="cursor-preview-layers">
              <InlineSvg
                v-if="cursorPreviews[key][state]?.layer2"
                :src="cursorPreviews[key][state].layer2"
                class="cursor-preview-layer2"
              />
              <img
                v-if="cursorPreviews[key][state]?.layer1 && key === 'EMOJI'"
                :src="cursorPreviews[key][state].layer1"
                class="cursor-preview-layer1"
              />
              <InlineSvg
                v-else-if="cursorPreviews[key][state]?.layer1"
                :src="cursorPreviews[key][state].layer1"
                class="cursor-preview-layer1"
              />
            </div>
          </div>
        </div>

        <div :class="['cursor-title', cursorType === key ? 'e-ui-b2-semibold' : 'e-ui-b2-regular']">
          {{ title }}
        </div>
      </div>
    </div>
  </ScrollableContainer>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import InlineSvg from 'vue-inline-svg';
import ScrollableContainer from '../components/ScrollableContainer.vue';
import { CursorAlternatives } from '../../enums.js';
import { TrackPageView } from '../../../store/actions/PortfolioActions.js';
defineEmits(['save', 'cancel', 'close']);

const store = useStore();
const route = useRoute();

const cursorAlternatives = CursorAlternatives;

// require() paths must be relative to THIS file so webpack can build the correct
// static context module. Sharing the helper from useCursorSvg.js would make
// webpack resolve the path relative to that file instead.
function getCursorIcon(type, state) {
  try {
    const plain = require(`../../../../images/cursors/${type}/${state}.svg`);
    return { layer1: plain, layer2: null };
  } catch (_) {}

  let layer1 = null, layer2 = null;
  try { layer1 = require(`../../../../images/cursors/${type}/${state}_1.svg`); } catch (_) {}
  try { layer2 = require(`../../../../images/cursors/${type}/${state}_2.svg`); } catch (_) {}
  if (layer1 || layer2) return { layer1, layer2 };

  return null;
}

// Pre-build a lookup so the template never calls getCursorIcon() more than once per state.
const cursorPreviews = Object.freeze(
  Object.fromEntries(
    Object.keys(CursorAlternatives).map(key => [
      key,
      { preview_1: getCursorIcon(key, 'preview_1'), preview_2: getCursorIcon(key, 'preview_2') },
    ])
  )
);

const cursorType = computed(() =>
  store.state.portfolio?.templateSettings?.cursorType || 'DEFAULT'
);

// Snapshot the cursor type on creation so we can restore it if the user
// navigates away without saving.
let originalCursorType = cursorType.value;

store.commit('setCursorPreviewMode', true);

onMounted(() => {
  store.commit('updateSidePanel', {
    ...route.meta.sidePanelSettings,
    previousSettings: JSON.parse(JSON.stringify(cursorType.value)),
    showInfoAboveCtas: true,
    infoAboveCtasMessage: 'Once you hit save, custom cursors only appear on your live site.',
  });

  TrackPageView(`/home${route.fullPath}`, 'Portfolio customization - Cursors');
});

onBeforeUnmount(() => {
  store.commit('setCursorPreviewMode', false);
  store.commit('updateSidePanel', { showInfoAboveCtas: false, infoAboveCtasMessage: null });

  // Restore original cursor if the user navigated away without saving
  if (originalCursorType !== null) {
    store.commit('updatePortfolioTemplateSettings', { cursorType: originalCursorType });
  }
});

function changeCursorPreset(key) {
  store.commit('updatePortfolioTemplateSettings', { cursorType: key });
  store.commit('updateSidePanel', { allowSave: true });
}

// Called by the parent side-panel via template refs / event forwarding
function triggerSave() {
  originalCursorType = null; // prevent beforeUnmount from restoring
  store.commit('setCursorPreviewMode', false);
}

function triggerCancel() {
  if (originalCursorType !== null) {
    store.commit('updatePortfolioTemplateSettings', { cursorType: originalCursorType });
  }
  store.commit('setCursorPreviewMode', false);
}

defineExpose({ triggerSave, triggerCancel });
</script>

<style scoped lang="scss">
.ufo-sepa--cop-list {
  display: flex;
  width: 100%;
  padding: 24px 30px 24px 30px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.cursor-preset-preview {
  display: flex;
  height: 80px;
  padding: 8px;
  align-items: center;
  gap: 16px;
  width: 100%;
  background-color: var(--asphalt80);
  flex-direction: row;
  justify-items: center;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background-color: var(--asphalt70);
  }

  &.selected {
    background-color: var(--asphalt60);
    .cursor-title {
      color: #FEFEFE;
    }
  }

  .cursor-title {
    color: #ADADAD;
  }
}

.cursor-preview {
  display: flex;
  width: 96px;
  height: 64px;
  padding: 8px 12px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  background: var(--asphalt90);
  border-radius: 8px;
  align-content: center;
  flex-direction: row;
  gap: 8px;

  .cursor-preset-preview:hover & {
    background: var(--asphalt80);
  }

  .cursor-preset-preview.selected & {
    background: rgba(255, 255, 255, 0.56);
  }
}

.cursor-preview-item {
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;

  .preview-label {
    font-size: 10px;
    color: var(--asphalt40);
    text-transform: uppercase;
    font-weight: 600;
  }
}

  .cursor-preview-layers {
  display: grid;
  place-items: center;
  overflow: visible;

  .cursor-preview-layer2,
  .cursor-preview-layer1 {
    grid-area: 1 / 1;
    display: block;
    overflow: visible;
    :deep(svg) {
      display: block;
      overflow: visible;
    }
  }
}
</style>
